/**
 * ПОЛОСА «ФАЙЛ» — PDF внизу поста (docs/release-2026-09-19.md §9.2), под полосой визуала и по
 * её ширине: файл уходит последним блоком поста, и его орган стоит там же, где стоит орган
 * картинки, — под постом, в порядке блоков сообщения (картинка сверху, файл снизу).
 *
 * ПУСТОГО СОСТОЯНИЯ У ПОЛОСЫ НЕТ — по тому же правилу, что у визуала: «файла нет» занимает ту
 * же шапку и тот же ряд кнопок, что «файл есть». Отсутствие, которое видно, перестаёт быть
 * вопросом «а файл вообще можно прикрепить?».
 *
 * ОБЪЯСНЕНИЯ — В ⓘ, А НЕ МЕЛКИМ ТЕКСТОМ (правило партии §1): в теле полосы только данные
 * (имя, размер, ход загрузки) и действия. Почему файл делает пост расширенным сообщением и
 * почему прикрепление — новая версия, говорит поповер рядом с действиями.
 *
 * ЗОНА СБРОСА — САМА ПОЛОСА, а не колонка поста: колонка — зона сброса картинки, и два
 * смысла у одного прямоугольника читались бы как угадайка. События перетаскивания полоса
 * дальше себя не пускает (`useDocumentUpload`). У версии, файл которой не меняется, зоны
 * сброса нет вовсе — ни подсветки, ни «Отпустите PDF»: вместо неё страж (`guardProps`),
 * который не отдаёт файл зоне картинки и называет настоящую причину.
 */

import { HUMAN_DOCUMENT_MAX_BYTES } from '@smm/contracts';
import type { JSX } from 'react';
import { fileSize } from '../../components/format.ts';
import { InfoPopover } from '../../components/InfoPopover.tsx';
import { IconFile } from '../../components/icons.tsx';
import type { DocumentStripProps } from '../contracts.ts';
import styles from './DocumentStrip.module.css';
import { documentLockReason } from './useDocumentUpload.ts';

const PHASE_TEXT = {
  save: 'Сохраняем версию с файлом…',
  remove: 'Убираем файл…',
} as const;

export function DocumentStrip({ model, upload }: DocumentStripProps): JSX.Element {
  // Имя и размер для строки — из `document_ref`: ради неё они там и повторены (`api/ui.ts`),
  // экран не лезет в подписываемую форму за подписью к значку. Дескриптор — запасной путь:
  // пару «дескриптор ↔ адрес» держит схема модели рендера, и без адреса файла не бывает.
  const signed = model.canonical.document ?? null;
  const ref = model.document_ref ?? null;
  const doc =
    ref !== null
      ? { file_name: ref.file_name, bytes: ref.bytes }
      : signed === null
        ? null
        : { file_name: signed.file_name, bytes: signed.bytes };
  // Причина — по состоянию версии: «выпущен ключ, снимите со слота» правда только у
  // запланированной, а у заменённой или опубликованной ни ключа, ни слота нет (ревью, ui-11).
  const lock = documentLockReason(model.status);
  const editable = lock === null;

  const headline = doc === null ? 'файла нет' : `PDF в версии v${String(model.version_no)}`;

  const pct = upload.progress === null ? null : Math.round(upload.progress * 100);
  const progressText =
    upload.phase === 'upload'
      ? `Загружаем «${upload.fileName ?? 'файл'}»${pct === null ? '…' : ` — ${String(pct)} %`}`
      : upload.phase === null
        ? null
        : PHASE_TEXT[upload.phase];

  const button = (label: string, onClick: () => void): JSX.Element => (
    <button
      type="button"
      className={upload.busy ? 'btn btn-sm btn-dis' : 'btn btn-ghost btn-sm'}
      disabled={upload.busy}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <section
      className={styles['strip']}
      aria-label="Файл поста"
      data-testid="document-strip"
      data-dropping={editable && upload.dragging ? '' : undefined}
      {...(editable ? upload.dropProps : upload.guardProps)}
    >
      <div className={styles['head']}>
        <span className="eyebrow">Файл</span>
        <span className="meta">{headline}</span>
        {lock === null ? null : (
          <span className="meta" title={`Файл этой версии не меняется: ${lock}.`}>
            · только чтение
          </span>
        )}
      </div>

      {editable && upload.dragging ? (
        <div className={styles['dropHint']}>Отпустите PDF — он встанет последним блоком поста</div>
      ) : null}

      {doc === null ? null : (
        <div className={styles['file']}>
          <span className={styles['fileHead']}>
            <span className={styles['fileIcon']} aria-hidden="true">
              <IconFile />
            </span>
            <span className={styles['fileName']} data-testid="document-name">
              {doc.file_name}
            </span>
          </span>
          <span className="meta" data-testid="document-size">
            {fileSize(doc.bytes)}
          </span>
          {/* Скачать, а не «открыть в новой вкладке»: документ маршрут медиа отдаёт как
              вложение (`Content-Disposition: attachment`), и вкладка только мигнула бы. */}
          {ref === null ? null : (
            <a
              className={styles['open']}
              href={`/api/v1/media/${ref.media_id}`}
              download={doc.file_name}
            >
              Скачать
            </a>
          )}
        </div>
      )}

      {progressText === null ? null : (
        <div className={styles['progress']}>
          <div
            className={styles['track']}
            role="progressbar"
            aria-label="Ход загрузки файла"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct ?? undefined}
          >
            <div
              className={pct === null ? `${styles['fill']} ${styles['fillBusy']}` : styles['fill']}
              style={pct === null ? undefined : { width: `${String(pct)}%` }}
            />
          </div>
          <span className={`meta ${styles['line']}`} data-testid="document-progress">
            {progressText}
          </span>
          {upload.phase === 'upload' ? (
            <button type="button" className={styles['cancel']} onClick={upload.cancel}>
              Отменить
            </button>
          ) : null}
        </div>
      )}

      {/* У версии «только чтение» ряда действий нет вовсе: ⓘ объясняет кнопки, которых нет, и
          одинокий значок в пустом ряду выглядел поломкой (снимок 19.09.2026). Причина запрета —
          в подсказке «только чтение» у заголовка полосы. */}
      {editable ? (
        <div className={styles['actions']}>
          {button(doc === null ? 'Прикрепить PDF…' : 'Заменить PDF…', upload.pick)}
          {doc === null ? null : button('Удалить файл', upload.remove)}
          <InfoPopover label="Как устроен файл поста">
            <span>
              PDF встаёт последним блоком поста: читатель канала видит значок, имя файла и размер и
              открывает его прямо в Telegram. Имя сервер приводит к тому виду, в каком его покажет
              Telegram: без пути, кавычек и знаков, которые Telegram заменяет пробелами, не длиннее
              64 знаков, с «.pdf» на конце. Утверждаете вы ровно то имя, которое увидит читатель.
            </span>
            <span>
              Пост с файлом уходит расширенным сообщением — карточки превью ссылки у него не будет.
            </span>
            <span>
              Файл входит в подписываемое содержание: прикрепление, замена и удаление создают новую
              версию, и её нужно утвердить заново.
            </span>
            <span>{`Только PDF, до ${fileSize(HUMAN_DOCUMENT_MAX_BYTES)}. Файл можно перетащить прямо на эту полосу.`}</span>
          </InfoPopover>
        </div>
      ) : null}

      {/* Клавиатурный путь загрузки: у зоны сброса роли ARIA нет, поэтому «Прикрепить PDF…» —
          настоящая кнопка, кликающая по этому полю. */}
      <input
        ref={upload.inputRef}
        type="file"
        accept={upload.accept}
        onChange={upload.onInputChange}
        style={{ display: 'none' }}
        data-testid="document-file-input"
      />

      {/* Строки хода, итога и отказа несут имя файла — у них `line`: имя переносится, а не
          распирает строку за рамку полосы (DocumentStrip.module.css). */}
      {upload.error === null ? null : (
        <div className="val val-err" role="alert">
          <span className={styles['line']}>{upload.error}</span>
        </div>
      )}
      {upload.notice === null ? null : (
        <div className="val val-info">
          <span className={styles['line']}>{upload.notice}</span>
        </div>
      )}
    </section>
  );
}
